import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CommandPalette from '@/components/navigation/CommandPalette';
import { buildVisibleNavGroups, layoutNavItems, type NavGroup } from '@/components/layoutNav';
import { useFeatureFlags } from '@/context/FeatureFlagContext';
import { useMe } from '@/context/MeContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';

type NavigationContextValue = {
  navGroups: NavGroup[];
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  commandPaletteOpen: boolean;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  isCompactNav: boolean;
  breakpoint: ReturnType<typeof useBreakpoint>['breakpoint'];
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;
};

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const { me } = useMe();
  const { flags } = useFeatureFlags();
  const { breakpoint, isCompactNav } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebarCollapsed] = useSidebarCollapsed();

  const navGroups = useMemo(
    () => buildVisibleNavGroups(layoutNavItems, me, flags),
    [me, flags],
  );

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((open) => !open), []);
  const openCommandPalette = useCallback(() => setCommandPaletteOpen(true), []);
  const closeCommandPalette = useCallback(() => setCommandPaletteOpen(false), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openCommandPalette();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openCommandPalette]);

  const value = useMemo(
    () => ({
      navGroups,
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      commandPaletteOpen,
      openCommandPalette,
      closeCommandPalette,
      isCompactNav,
      breakpoint,
      sidebarCollapsed,
      toggleSidebarCollapsed,
    }),
    [
      navGroups,
      drawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      commandPaletteOpen,
      openCommandPalette,
      closeCommandPalette,
      isCompactNav,
      breakpoint,
      sidebarCollapsed,
      toggleSidebarCollapsed,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
      <CommandPalette
        open={commandPaletteOpen}
        onClose={closeCommandPalette}
        navGroups={navGroups}
      />
    </NavigationContext.Provider>
  );
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return ctx;
}
